import React, { Fragment, useState, useEffect } from "react";
import {
  Card,
  Select,
  Button,
  Dropdown,
  Menu,
  Affix,
  Popconfirm,
  Typography,
  Breadcrumb,
  Input,
  Space,
  Tabs,
} from "antd";
import ListTable from "../../../common/ListTable";
import Header from "../../Common/Header";
import { isMobileView } from "../../Common/AppHelper";
import { Link, useLocation } from "react-router-dom";
import {
  MoreOutlined,
  EditOutlined,
  DeleteTwoTone,
  EyeOutlined,
  ExportOutlined,
} from "@ant-design/icons";
import { PLANNING_REQUESTS } from "../../../apiServices/API_ENDPOINTS";
import { getData, deleteData, postData } from "../../../apiServices/common";
import CustomFilter from "../../Common/CustomFilter";
import { alertPop } from "../../../apiServices/common/helper";
import {
  cglFormatDate,
  commaSeparateNumber,
  hasPermission,
} from "../../../utils/functions";
import { NO_PERMISSION_REQUIRED } from "../../../routes/permissions";
import moment from "moment";

const PLAN_STATUS_TABS = [
  { key: "Pending", label: "Pending" },
  { key: "Planned", label: "Plan" },
];

const isPpcUser = () => {
  try {
    const roleInfo = JSON.parse(localStorage.getItem("roleInfo") || "{}");
    return String(roleInfo?.name || "").trim().toUpperCase() === "PPC";
  } catch {
    return false;
  }
};

const PlanningRequests = (props) => {
  // Props
  const { width } = props;
  const isPpc = isPpcUser();

  // Router
  const location = useLocation();
  const routerPath = location ? location.pathname : null;

  //   States
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );

  const [planningRequestList, setPlanningRequestList] = useState(null);
  const [totalData, setTotalData] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [planStatus, setPlanStatus] = useState(PLAN_STATUS_TABS[0].key);
  const [loading, setLoading] = useState(false);
  const [rangeData, setRangeData] = useState();
  const [fetchError, setFetchError] = useState(null);

  // Antd
  const { Title } = Typography;

  // Filter open/close status update in localStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const inquiryStatus = {
    //  Inquiry ==> 0, Contracted ==> 1, Closed ==> 2, Capacity Booking ==> 3
    0: "Inquiry",
    1: "Contracted",
    2: "Closed",
    3: "Capacity Booking",
  };

  const columns = [
    {
      title: "Request Date",
      dataIndex: "request_date",
      // key: "request_date",
      render: (value) => cglFormatDate(value),
    },
    {
      title: "Inquiry",
      dataIndex: "plan_request_detail",
      key: "buyer_inquiry_id",
      render: (planRequestDetail) => {
        if (!planRequestDetail || planRequestDetail.length === 0) return "N/A";

        const uniqueInquiries = new Set();
        planRequestDetail.forEach((detail) => {
          const inquiryNo = detail.buyer_inquiry?.inquiry_no;
          if (inquiryNo) uniqueInquiries.add(inquiryNo);
        });

        const uniqueInquiryArray = Array.from(uniqueInquiries);

        if (uniqueInquiryArray.length <= 1) {
          return uniqueInquiryArray[0] || "N/A";
        }

        return (
          <ol style={{ paddingLeft: "20px", margin: 0 }}>
            {uniqueInquiryArray.map((inquiryNo, index) => (
              <li key={index}>{inquiryNo}</li>
            ))}
          </ol>
        );
      },
    },
    {
      title: "Style",
      dataIndex: "plan_request_detail",
      key: "style",
      render: (planRequestDetail) => {
        if (!planRequestDetail || planRequestDetail.length === 0) return "N/A";

        const uniqueStyles = new Set();
        planRequestDetail.forEach((detail) => {
          const styleNo =
            detail.buyer_inquiry?.style_inquiry_map?.style_info?.style_no;
          if (styleNo) uniqueStyles.add(styleNo);
        });

        const uniqueStyleArray = Array.from(uniqueStyles);

        if (uniqueStyleArray.length <= 1) {
          return uniqueStyleArray[0] || "N/A";
        }

        return (
          <ol style={{ paddingLeft: "20px", margin: 0 }}>
            {uniqueStyleArray.map((styleNo, index) => (
              <li key={index}>{styleNo}</li>
            ))}
          </ol>
        );
      },
    },
    {
      title: "Order Qty",
      dataIndex: "plan_request_detail",
      key: "order_qty",
      render: (planRequestDetail) => {
        if (!planRequestDetail || planRequestDetail.length === 0) return "N/A";

        const uniqueOrderQtys = new Set();
        planRequestDetail.forEach((detail) => {
          const orderQty =
            detail.buyer_inquiry?.order_quantity !== undefined &&
              detail.buyer_inquiry?.order_quantity !== null
              ? detail.buyer_inquiry?.order_quantity
              : detail.request_qty;
          if (orderQty !== undefined && orderQty !== null) {
            uniqueOrderQtys.add(orderQty);
          }
        });

        const uniqueOrderQtyArray = Array.from(uniqueOrderQtys);

        if (uniqueOrderQtyArray.length <= 1) {
          return uniqueOrderQtyArray[0] !== undefined
            ? commaSeparateNumber(uniqueOrderQtyArray[0])
            : "N/A";
        }

        return (
          <ol style={{ paddingLeft: "20px", margin: 0 }}>
            {uniqueOrderQtyArray.map((qty, index) => (
              <li key={index}>{commaSeparateNumber(qty)}</li>
            ))}
          </ol>
        );
      },
    },
    {
      title: "PCD",
      dataIndex: "plan_request_detail",
      key: "pcd",
      render: (planRequestDetail) => {
        if (!planRequestDetail || planRequestDetail.length === 0) return "N/A";

        const uniquePcds = new Set();
        planRequestDetail.forEach((detail) => {
          const pcd = detail.pcd;
          if (pcd) uniquePcds.add(pcd);
        });

        const uniquePcdArray = Array.from(uniquePcds);

        if (uniquePcdArray.length <= 1) {
          return uniquePcdArray[0]
            ? cglFormatDate(uniquePcdArray[0]) || uniquePcdArray[0]
            : "N/A";
        }

        return (
          <ol style={{ paddingLeft: "20px", margin: 0 }}>
            {uniquePcdArray.map((pcd, index) => (
              <li key={index}>{cglFormatDate(pcd) || pcd}</li>
            ))}
          </ol>
        );
      },
    },
    {
      title: "Shipment Date",
      dataIndex: "plan_request_detail",
      key: "shipment_date",
      render: (planRequestDetail) => {
        if (!planRequestDetail || planRequestDetail.length === 0) return "N/A";

        const uniqueShipmentDates = new Set();
        planRequestDetail.forEach((detail) => {
          const shipmentDate = detail.shipment_date;
          if (shipmentDate) uniqueShipmentDates.add(shipmentDate);
        });

        const uniqueShipmentDateArray = Array.from(uniqueShipmentDates);

        if (uniqueShipmentDateArray.length <= 1) {
          return uniqueShipmentDateArray[0]
            ? cglFormatDate(uniqueShipmentDateArray[0]) ||
            uniqueShipmentDateArray[0]
            : "N/A";
        }

        return (
          <ol style={{ paddingLeft: "20px", margin: 0 }}>
            {uniqueShipmentDateArray.map((shipmentDate, index) => (
              <li key={index}>
                {cglFormatDate(shipmentDate) || shipmentDate}
              </li>
            ))}
          </ol>
        );
      },
    },
    {
      title: "Request by",
      dataIndex: ["request_by", "name"],
      key: "request_by",
    },
    {
      title: "Order Status",
      dataIndex: "order_status",
      key: "order_status",
      render: (orderStatus, record) => {
        let status = orderStatus;
        if (!status && record?.plan_request_detail?.length) {
          status = record.plan_request_detail.find(
            (d) => d.buyer_inquiry?.order_status,
          )?.buyer_inquiry?.order_status;
        }
        if (status === "Yes" || status?.toLowerCase() === "yes") return "Confirm";
        if (status === "Projection" || status?.toLowerCase() === "projection") return "Projection";
        return "N/A";
      },
    },
    {
      title: "Request Status",
      dataIndex: "plan_request_detail",
      key: "inquiry_status",
      render: (planRequestDetail) => {
        if (!planRequestDetail || planRequestDetail.length === 0) return "N/A";

        const uniqueStatuses = new Set();
        planRequestDetail.forEach((detail) => {
          const status = detail.buyer_inquiry?.inquiry_status;
          if (status !== undefined) uniqueStatuses.add(status);
        });

        const uniqueStatusArray = Array.from(uniqueStatuses);

        if (uniqueStatusArray.length === 1) {
          return inquiryStatus[uniqueStatusArray[0]] || "N/A";
        }

        return (
          <ol style={{ paddingLeft: "20px", margin: 0 }}>
            {uniqueStatusArray.map((status, index) => (
              <li key={index}>{inquiryStatus[status] || "N/A"}</li>
            ))}
          </ol>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "plan_request_detail",
      key: "status",
      render: (planRequestDetail) => {
        if (!planRequestDetail || planRequestDetail.length === 0) return "N/A";

        const uniquePlannedStatuses = new Set();
        planRequestDetail.forEach((detail) => {
          const plannedStatus = detail.buyer_inquiry?.is_planned;
          if (plannedStatus) uniquePlannedStatuses.add(plannedStatus);
        });

        const uniquePlannedStatusArray = Array.from(uniquePlannedStatuses);

        if (uniquePlannedStatusArray.length === 1) {
          return uniquePlannedStatusArray[0];
        }

        return (
          <ol style={{ paddingLeft: "20px", margin: 0 }}>
            {uniquePlannedStatusArray.map((plannedStatus, index) => (
              <li key={index}>{plannedStatus}</li>
            ))}
          </ol>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: "10%",
      render: (_, record) => (
        <div className="cursore-pointer">
          <Dropdown overlay={menu(record)}>
            <MoreOutlined style={{ fontSize: "20px" }} />
          </Dropdown>
        </div>
      ),
    },
  ];

  const getAllSlug = async (filterValues) => {
    setLoading(true);
    setFetchError(null);

    const bodyData = filterValues;
    const response = await getData(PLANNING_REQUESTS, false, bodyData);
    // console.log('filterValues', filterValues);
    // console.log('response', response);
    if (response && response?.data?.code === 200) {
      setPlanningRequestList(response?.data?.data?.data);
      setTotalData(response?.data?.data?.total);
      setRangeData(response?.data?.data?.per_page);
    } else {
      alertPop(
        "error",
        response?.messages ? response?.messages : "Something Went Wrong!",
      );
      setFetchError("Could not load this list.");
    }

    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    getAllSlug({ page: currentPage, plan_status: planStatus });
  }, [currentPage, planStatus]);

  const onPlanStatusChange = (key) => {
    setPlanningRequestList(null);
    setCurrentPage(1);
    setPlanStatus(key);
  };

  const removePlanningRequest = async (value) => {
    const response = await deleteData(`${PLANNING_REQUESTS}/${value}`);
    if (response && response?.code === 200) {
      alertPop("success", "Deleted Successfully!");
      getAllSlug({ page: currentPage, plan_status: planStatus });
    } else {
      alertPop(
        "error",
        response?.data?.message
          ? response?.data?.message[0]
          : "Something Went Wrong!",
      );
    }
  };

  const getPlanEntryRedirectData = (data) => {
    const details = data?.plan_request_detail || [];
    const uniqueInquiriesMap = new Map();

    details.forEach((detail) => {
      const inqId = detail?.inquiry_id || detail?.buyer_inquiry?.id;
      if (inqId && !uniqueInquiriesMap.has(inqId)) {
        uniqueInquiriesMap.set(inqId, detail);
      }
    });

    if (uniqueInquiriesMap.size === 1) {
      const singleDetail = uniqueInquiriesMap.values().next().value;
      const inquiryId = singleDetail?.inquiry_id || singleDetail?.buyer_inquiry?.id;
      const inquiryNo = singleDetail?.buyer_inquiry?.inquiry_no;
      const orderQty =
        singleDetail?.buyer_inquiry?.order_quantity !== undefined &&
          singleDetail?.buyer_inquiry?.order_quantity !== null
          ? singleDetail?.buyer_inquiry?.order_quantity
          : singleDetail?.request_qty;
      const shipmentDate =
        singleDetail?.shipment_date ||
        singleDetail?.buyer_inquiry?.expected_delivery_date;

      const params = new URLSearchParams();
      if (inquiryId) params.append("inquiry_id", inquiryId);
      if (inquiryNo) params.append("inquiry_no", inquiryNo);
      if (orderQty !== undefined && orderQty !== null && orderQty !== "") {
        params.append("order_qty", orderQty);
      }
      if (shipmentDate) {
        const formattedDate = moment(shipmentDate).isValid()
          ? moment(shipmentDate).format("YYYY-MM-DD")
          : shipmentDate;
        params.append("shipment_date", formattedDate);
      }

      return {
        url: `/production-planning/plan-entry?${params.toString()}`,
        state: {
          inquiry_id: inquiryId,
          inquiry_no: inquiryNo,
          order_qty: orderQty,
          shipment_date: shipmentDate,
          buyer_inquiry: singleDetail?.buyer_inquiry,
        },
      };
    }

    return {
      url: "/production-planning/plan-entry",
      state: null,
    };
  };

  const menu = (data) => {
    const { url: planEntryUrl, state: planEntryState } =
      getPlanEntryRedirectData(data);

    const items = isPpc
      ? [
          {
            label: (
              <Link to={planEntryUrl} state={planEntryState}>
                <ExportOutlined /> Plan Entry
              </Link>
            ),
            key: "1",
            disabled: !hasPermission([NO_PERMISSION_REQUIRED]),
          },
        ]
      : [
          {
            label: (
              <Link
                to={`/production-planning/planningRequests-update/${data.id}`}
              >
                <EditOutlined /> Edit
              </Link>
            ),
            key: "0",
            disabled: !hasPermission([NO_PERMISSION_REQUIRED]),
          },
          {
            label: (
              <Popconfirm
                title="Are you sure you want to delete?"
                onConfirm={() => removePlanningRequest(data.id)}
              >
                <DeleteTwoTone /> Delete
              </Popconfirm>
            ),
            key: "2",
            disabled: !hasPermission([NO_PERMISSION_REQUIRED]),
          },
        ];

    return <Menu items={items} />;
  };

  const paginate = (page) => {
    setCurrentPage(page);
  };

  const handleFilter = () => {
    setShowFilter((prev) => !prev);
  };

  const extra = (
    <Space>
      {/* <Button
        type={showFilter ? "danger" : "primary"}
        size="small"
        onClick={() => setShowFilter((prev) => !prev)}
        style={{ float: "right" }}
      >
        {`${showFilter ? "Close" : "Open"} Filter Option`}
      </Button> */}
      {!isPpc && (
        <Button key="3" size="small" type="primary">
          <Link to="/production-planning/planningRequests/create">Add New</Link>
        </Button>
      )}
    </Space>
  );

  return (
    <Fragment>
      <Affix offsetTop={0}>
        <Card
          className="p-fixed"
          style={{ width: `${isMobileView() ? "100%" : `${width}px`}` }}
        >
          <Header
            title={<Title level={5}>Planning Request</Title>}
            actions={extra}
          />
        </Card>
      </Affix>

      <div className="containt-body">
        <Card>
          {/* <CustomFilter
            showFilter={showFilter}
            setShowFilter={setShowFilter}
            tableData={planningRequestList}
            fetchTableData={getAllSlug}
            filterName="planning_Request"
            // filterName="cost_estimate"
          /> */}
          <Tabs activeKey={planStatus} onChange={onPlanStatusChange} items={PLAN_STATUS_TABS} />
          <ListTable
            loading={loading}
            error={fetchError}
            onRetry={() => getAllSlug({ page: currentPage, plan_status: planStatus })}
            emptyText="No records to show yet."
            dataSource={planningRequestList}
            columns={columns}
            pagination={{
              showSizeChanger: false,
              current: currentPage,
              total: totalData,
              pageSize: rangeData,
              onChange: (page) => paginate(page),
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
            }}
          />
        </Card>
      </div>
    </Fragment>
  );
};

export default PlanningRequests;
